import type { CategoryContent } from './row.js';

export const PAINTERS: CategoryContent = {
  bscba: [
    [
      'Which Renaissance painter is most famous for his detailed dome frescoes in the Brancacci Chapel?',
      [
        'Masaccio',
        'Raphael',
        'Donatello',
        'Ghirlandaio',
      ],
      0,
      'Masaccio\'s work in the Brancacci Chapel (1425–1428) pioneered linear perspective and anatomical accuracy, establishing him as a foundational figure in Renaissance art.',
    ],
    [
      'What painting technique involves applying pigment to wet plaster, binding it chemically as the surface dries?',
      [
        'Fresco',
        'Tempera',
        'Encaustic',
        'Glazing',
      ],
      0,
      'Fresco (Italian: "fresh") is the only paint technique where pigment becomes part of the plaster itself, making it extremely durable but also unforgiving to errors.',
    ],
    [
      'Which late-19th-century movement prioritized light, color, and the fleeting moment over narrative detail?',
      [
        'Impressionism',
        'Romanticism',
        'Symbolism',
        'Baroque',
      ],
      0,
      'Impressionists like Monet and Renoir broke with academic tradition by painting outdoors (plein air) and dissolving form into color and light effects.',
    ],
    [
      'Who painted "The Starry Night"?',
      [
        'Vincent van Gogh',
        'Paul Cézanne',
        'Henri de Toulouse-Lautrec',
        'Édouard Manet',
      ],
      0,
      'Van Gogh\'s 1889 masterpiece uses swirling brushstrokes and vivid color to depict a night sky over a French village, embodying Post-Impressionist style.',
    ],
    [
      'Which transparent paint layer, applied over dried underpainting, adds luminosity and depth?',
      [
        'Glaze',
        'Impasto',
        'Scumble',
        'Grisaille',
      ],
      0,
      'Glazing involves thin, translucent coats of paint that allow underlying layers to show through, creating optical color mixing and depth in Old Master paintings.',
    ],
    [
      'What is the primary support surface for most European oil paintings since the Renaissance?',
      [
        'Canvas',
        'Wooden panel',
        'Copper sheet',
        'Parchment',
      ],
      0,
      'Canvas became the standard for oil painting after c. 1500 because it was cheaper than wood panels, lighter, and easier to transport and store.',
    ],
    [
      'Which movement rejected academic naturalism in favor of emotional expression and vivid, non-naturalistic color?',
      [
        'Expressionism',
        'Realism',
        'Neoclassicism',
        'Academicism',
      ],
      0,
      'Expressionists (early 20th century) like Kirchner and Kandinsky prioritized subjective feeling and distorted form to convey psychological or emotional states.',
    ],
    [
      'Who painted "The Birth of Venus"?',
      [
        'Sandro Botticelli',
        'Leonardo da Vinci',
        'Michelangelo',
        'Brunelleschi',
      ],
      0,
      'Botticelli\'s c. 1485 masterpiece, housed in the Uffizi Gallery, exemplifies Renaissance beauty with its fluid lines and mythological subject matter.',
    ],
    [
      'What early tempera technique involved binding pigment with egg yolk, producing matte finishes?',
      [
        'Egg tempera',
        'Oil-alkyd',
        'Acrylic',
        'Fresco secco',
      ],
      0,
      'Egg tempera was the dominant painting medium before the Renaissance; pigments mixed with egg yolk dried quickly and could not be blended like oil paint.',
    ],
    [
      'Which 17th-century art movement dramatized light and shadow for emotional intensity?',
      [
        'Baroque',
        'Rococo',
        'Mannerism',
        'High Renaissance',
      ],
      0,
      'Baroque painters like Caravaggio used chiaroscuro (stark light-dark contrast) and dynamic composition to create theatrical, emotionally charged scenes.',
    ],
    [
      'What is the thick, visibly textured application of paint that creates physical dimension on the canvas?',
      [
        'Impasto',
        'Sfumato',
        'Chiaroscuro',
        'Pentimento',
      ],
      0,
      'Impasto is visible brushwork or palette-knife application where paint sits thickly on the surface, catching light and creating tactile texture.',
    ],
    [
      'Who is the Dutch Post-Impressionist most famous for sunflower and wheat field paintings?',
      [
        'Vincent van Gogh',
        'Paul Gauguin',
        'Georges Seurat',
        'Edvard Munch',
      ],
      0,
      'Van Gogh created his sunflower series in Arles, France (1888–1889); the paintings use symbolism and expressive color to convey beauty and meaning.',
    ],
    [
      'What composition principle places the subject off-center at approximately one-third of the frame?',
      [
        'Rule of thirds',
        'Golden section',
        'Linear perspective',
        'Bilateral symmetry',
      ],
      0,
      'The rule of thirds, derived from Renaissance proportions, creates visual balance and interest by positioning focal points away from the center.',
    ],
    [
      'Which Italian Renaissance master painted "The School of Athens" in the Vatican?',
      [
        'Raphael',
        'Michelangelo',
        'Leonardo da Vinci',
        'Botticelli',
      ],
      0,
      'Raphael\'s monumental fresco (1509–1511) in the Vatican\'s Stanza della Segnatura depicts classical philosophers in harmonious Renaissance composition.',
    ],
    [
      'What term describes the gradual, almost imperceptible blending of tones to create soft transitions?',
      [
        'Sfumato',
        'Chiaroscuro',
        'Tenebrism',
        'Pointillism',
      ],
      0,
      'Sfumato, pioneered by Leonardo da Vinci, blurs edges and tones so subtly that contours seem to dissolve, creating atmospheric perspective.',
    ],
  ],
  msc: [
    [
      'In early Netherlandish painting, what revolutionary technique allowed painters to layer transparent oil glazes with unprecedented clarity and luminosity?',
      [
        'Layering thin, transparent oil glazes',
        'The Venetian method of applying tempera underneath oil',
        'The use of walnut oil instead of linseed oil',
        'The pre-mixing of pigments with powdered glass',
      ],
      0,
      'Jan van Eyck and his contemporaries perfected the oil glaze technique, allowing minute detail and jewel-like color that tempera could not match, transforming European painting.',
    ],
    [
      'What does "sfumato" specifically require technically that "chiaroscuro" does not?',
      [
        'Imperceptible tonal transitions',
        'Dramatic contrast between light and shadow zones',
        'A cool color in shadow and warm in light',
        'Multiple preparatory underdrawings',
      ],
      0,
      'Sfumato demands optical blending where tones graduate so smoothly edges vanish; chiaroscuro tolerates visible shadow boundaries, making sfumato the more technically demanding effect.',
    ],
    [
      'Which Italian master\'s studio perfected the technique of having apprentices block in compositions while the master refined faces and critical details?',
      [
        'Raphael',
        'Michelangelo',
        'Caravaggio',
        'Parmigianino',
      ],
      0,
      'Raphael\'s workshop operated on this division of labor; his students (Giulio Romano, Giovanni da Udine) handled preparatory work, accelerating production of commissions.',
    ],
    [
      'What is the standard preparatory layer for oil painting on canvas that seals the weave and provides a stable ground?',
      [
        'Gesso',
        'Varnish',
        'Linseed oil alone',
        'White lead paint',
      ],
      0,
      'Gesso, a traditional ground of calcium sulfate and animal glue, creates a non-absorbent surface that prevents oil from soaking into canvas fibers and ensures paint adhesion.',
    ],
    [
      'In Renaissance perspective theory, what is the vanishing point where orthogonal lines converge in linear perspective?',
      [
        'Where parallel lines meet on the horizon',
        'The farthest visible object in the painting',
        'The brightest area of the composition',
        'The precise center of the canvas',
      ],
      0,
      'The vanishing point, established mathematically by Brunelleschi and theorized by Alberti, creates the illusion of three-dimensional space on a flat surface.',
    ],
    [
      'Which 16th-century Venetian master pioneered the practice of building form primarily through color rather than line?',
      [
        'Titian',
        'Tintoretto',
        'Veronese',
        'Paolo Uccello',
      ],
      0,
      'Titian developed the "painterly" approach, emphasizing color harmony and brushwork over precise linear drawing, influencing all subsequent European painting traditions.',
    ],
    [
      'What does "pentimento" refer to in painting conservation and art history?',
      [
        'A trace of an earlier composition',
        'A crack in old paint caused by temperature change',
        'The signature or monogram hidden in a painting',
        'The removal of varnish during restoration',
      ],
      0,
      'Pentimento (Italian: "repentance") shows an artist\'s second thoughts; X-radiography often reveals earlier compositional versions hidden under final paint layers.',
    ],
    [
      'Which Northern Renaissance painter developed the technique of white-line borders around figures to enhance contour and isolation?',
      [
        'Albrecht Dürer',
        'Lucas Cranach',
        'Matthias Grünewald',
        'Hans Burgkmair',
      ],
      0,
      'Dürer adapted this technique from Flemish practice, using fine white or light outlines to define edges crisply, a distinctive mark of Northern Renaissance panel painting.',
    ],
    [
      'What is the thin underpainting layer in oil painting, often executed in monochrome, that establishes tonal values and form?',
      [
        'Underpaint',
        'Imprimatura',
        'Glaze layer',
        'Wash',
      ],
      0,
      'Underpainting maps values and composition before glazes; sinopia (for fresco) is the preliminary drawing on the arriccio layer that guides the final fresco work.',
    ],
    [
      'Which Baroque painter pioneered "tenebrism," an extreme form of chiaroscuro with almost entire scenes in darkness?',
      [
        'Caravaggio',
        'Artemisia Gentileschi',
        'Rembrandt',
        'Peter Paul Rubens',
      ],
      0,
      'Caravaggio\'s tenebrism isolates illuminated figures against near-total darkness, creating psychological drama and directing the viewer\'s gaze with almost theatrical light.',
    ],
    [
      'What is "distemper" painting, a technique distinct from tempera, and where was it most commonly used historically?',
      [
        'A water-based paint using hide glue',
        'A technique using egg and oil combined',
        'A fresco variation using lime and sand',
        'An encaustic-tempera hybrid from 15th-century Italy',
      ],
      0,
      'Distemper (size-based) was valued for theatrical scenery and decorative murals where durability was less critical than in panel or canvas painting; it remains fugitive.',
    ],
    [
      'In High Renaissance composition, what is the principle of "figura serpentinata" or the serpentine figure?',
      [
        'An elongated, twisting figural pose',
        'A geometric arrangement of multiple figures in diagonal lines',
        'The placement of figures in strict bilateral symmetry',
        'The use of green and blue tones to create visual serpentine patterns',
      ],
      0,
      'Serpentine figures, exemplified in Mannerism, use a twisting contrapposto that elongates and curves the body for elegant, dynamic compositions beyond High Renaissance restraint.',
    ],
    [
      'What is the primary reason Renaissance and Baroque painters preferred linseed oil over other binding agents for oil painting?',
      [
        'It dries through oxidation into a hard, durable layer',
        'It has a faster drying time than walnut oil',
        'It prevents cracking better than other oils',
        'It produces a glossy surface without additional varnishing',
      ],
      0,
      'Linseed oil oxidizes (polymerizes) at a controlled rate, creating a strong, flexible film that doesn\'t darken significantly; other oils were avoided for inferior durability.',
    ],
    [
      'Which iconographic symbol appears repeatedly in Northern Renaissance painting to represent divine presence or sanctity?',
      [
        'Gold background',
        'The absence of shadows',
        'Red pigment exclusively',
        'Water reflections',
      ],
      0,
      'Gold leaf and gold paint backgrounds, a Medieval legacy continued in Northern Renaissance, signified sacred space and the immaterial divine realm distinct from earthly naturalism.',
    ],
    [
      'What does "intonaco" refer to in fresco technique, and why is its quality critical?',
      [
        'The final wet plaster layer',
        'The preparatory charcoal underdrawing',
        'The dry sand and lime mortar base layer',
        'The color pigment mixed into plaster',
      ],
      0,
      'Intonaco must be evenly hydrated and thin enough to allow chemical bonding; poor quality prevents proper pigment adhesion, causing fresco deterioration and flaking.',
    ],
  ],
  phd: [
    [
      'In Late Gothic and Early Netherlandish painting, explain the transition from tempera to oil and its implications for sculptural illusionism.',
      [
        'Oil\'s slow drying enabled glaze layering for optical depth',
        'Tempera dried too quickly, preventing the detailed underpainting necessary for anatomy',
        'Oil pigments were simply more vibrant than tempera pigments chemically',
        'Oil allowed artists to paint larger canvases than tempera supports',
      ],
      0,
      'Flemish masters like Rogier van der Weyden used oil glazes to model form subtly through color without line, achieving painterly equivalence to sculpture\'s three-dimensionality.',
    ],
    [
      'What technical distinction separates Titian\'s late "broken color" technique from earlier Venetian colorism in terms of brushwork and optical mixing?',
      [
        'Visible brushstrokes optically mixed by the viewer\'s eye',
        'Early Titian mixed all colors thoroughly on the palette',
        'Broken color requires a monochromatic underpainting visible throughout',
        'The technique is purely a matter of pigment choice, not application method',
      ],
      0,
      'Titian\'s late works apply paint in loose, parallel strokes where colors intermix in the viewer\'s eye; this optical approach influenced colorists centuries later.',
    ],
    [
      'Why was lead white universally preferred over other white pigments in Old Master oil painting, despite its known toxicity?',
      [
        'It oxidizes rapidly, drying fast without yellowing',
        'It reflects more light than other pigments',
        'It was the only white pigment available in Renaissance Europe',
        'It prevents the painting from cracking over time',
      ],
      0,
      'Lead white oxidizes rapidly with heat, accelerating paint film formation and preventing yellowing that other whites exhibited; its toxicity was considered acceptable given superior performance.',
    ],
    [
      'In High Renaissance narrative composition, what structural principle did Raphael employ to unify multiple focal points without hierarchical domination?',
      [
        'Triangular grouping of figures at staggered depths',
        'Central vanishing point with all elements radiating outward',
        'Complete symmetry around a vertical axis',
        'Diagonal horizon lines forcing attention to the upper left',
      ],
      0,
      'Raphael\'s triangular figural arrangements (evident in Vatican frescoes) guide the eye through interconnected planes; this geometric harmony became canonical Renaissance composition.',
    ],
    [
      'What is the specific role of the "arriccio" and "sinopia" layers in true fresco technique, and how do they affect artist workflow?',
      [
        'Arriccio receives the sinopia underdrawing',
        'They are the same layer with different names',
        'Only one is used in Classical Roman fresco',
        'They are applied after the painted layer dries',
      ],
      0,
      'Sinopia on arriccio allows the artist to plan detailed composition; once intonaco is applied, changes are impossible, making sinopia the essential planning stage.',
    ],
    [
      'How did Northern Renaissance painters use color gradation to achieve atmospheric perspective, in contrast to Italian linear perspective?',
      [
        'Blue-gray tinting in distant areas',
        'They used the same techniques as Italian painters',
        'Northern painters rejected perspective entirely',
        'Only Italian painters understood color perspective',
      ],
      0,
      'Northern Renaissance exploited color shift toward cooler tones and lower saturation for distance, achieving perspective optically; this complemented rather than replaced linear perspective.',
    ],
    [
      'What is "fustian" in historical paint terminology, and what pigment instability did its use risk?',
      [
        'Dyes that fade and shift hue',
        'A technical term for impasto application',
        'A specific binding medium made from cotton',
        'An archaic term for glazing technique',
      ],
      0,
      'Fustian pigments (organic lakes like madder) are fugitive; dealers sometimes sold them as permanent pigments, causing color loss that appears as hue shifts or fading in old paintings.',
    ],
    [
      'What specific innovations in studio lighting and paint formulation did Caravaggio\'s tenebrism technique require?',
      [
        'Fast-drying, opaque paint with high tinting strength',
        'He invented a new pigment specifically for tenebrism',
        'The technique required only darker pigments, not special formulation',
        'It was purely a matter of subject matter, not technical adjustment',
      ],
      0,
      'Caravaggio\'s high-contrast style required confident value placement from the start; he used lead white generously for opaque darks and worked under directional studio light.',
    ],
    [
      'What does the X-radiography evidence reveal about Rembrandt\'s correction patterns that suggests a deliberate revision approach distinct from other Baroque painters?',
      [
        'Extensive, methodical pentimenti',
        'Rembrandt never made corrections',
        'His pentimenti are random and careless',
        'X-radiography cannot reveal Rembrandt\'s techniques',
      ],
      0,
      'X-rays show Rembrandt\'s systematic reposing of figures and recalibration of gestures; this suggests a generative process favoring spontaneous revision over fixed preparatory drawing.',
    ],
    [
      'In Baroque iconography, explain the symbolic function of specific color sequences (e.g., red-blue-gold) in Marian imagery.',
      [
        'Passion (red), virtue (blue), majesty (gold)',
        'Colors were arbitrary and had no symbolic meaning',
        'Only blue had religious significance',
        'Color symbolism changed every century with no consistency',
      ],
      0,
      'Marian color symbolism was codified in Medieval and Renaissance iconographic texts; red-blue-gold combinations appeared deliberately to anchor theological meaning in visual form.',
    ],
    [
      'What conservation evidence (craquelure patterns, paint layer composition) reveals about the difference between authentic Old Masters and 18th-century copies or alterations?',
      [
        'Age-specific craquelure matching the period',
        'All old paintings have identical craquelure',
        'Craquelure is purely decorative and meaningless',
        'Conservation techniques cannot detect forgeries',
      ],
      0,
      'Age-specific craquelure, pigment availability timelines, and binding-media oxidation patterns provide chronological fingerprints; anachronisms expose copies or extensive later reworking.',
    ],
    [
      'How did the Renaissance application of Vitruvian proportions differ from Medieval symbolic geometric schemes?',
      [
        'Vitruvian ratios from human anatomy, not symbolic geometry',
        'Both are identical',
        'Vitruvian proportions were invented in the Renaissance with no Classical source',
        'Medieval painters never used geometric schemes',
      ],
      0,
      'Brunelleschi and Alberti adapted Vitruvius\'s human-proportioned ratios (e.g., 1:2:3 spacing), replacing Medieval symbolic geometry with ratios grounded in observed anatomical harmony.',
    ],
    [
      'In the restoration of a Baroque canvas, what conservation evidence would distinguish between original paint, early varnish discoloration, and later inpainting?',
      [
        'IR and UV reveal varnish age and inpainting',
        'There is no way to distinguish these',
        'All old paintings look identical under imaging',
        'Only chemical analysis works, imaging does not help',
      ],
      0,
      'IR shows original paint\'s maturity, UV fluorescence marks modern inpainting materials, and stratigraphic analysis reveals varnish layers; these combined diagnostic tools map restoration history.',
    ],
    [
      'What does the "Venetian colorism" vs. "Florentine drawing" dichotomy in Renaissance art criticism actually describe in terms of underlying artistic philosophy?',
      [
        'Florentine linear form vs. Venetian color harmony',
        'Venetian painters were worse at drawing than Florentine painters',
        'It\'s purely a geographical convenience with no philosophical basis',
        'Both schools used identical methods',
      ],
      0,
      'The dichotomy reflects Renaissance humanism\'s intellectual vs. sensory poles: Florence privileged rational design (disegno), Venice celebrated optical color and visual pleasure (colorito).',
    ],
    [
      'What technical purpose did "wash" layers serve in watercolor and gouache preparation, distinguishing them from tempera and oil techniques?',
      [
        'Washes need absorbent paper, unlike gesso grounds',
        'Washes are the same as glazes',
        'Watercolor and gouache are oils',
        'Wash technique requires lead white like oil painting',
      ],
      0,
      'Paper\'s absorbency allows transparent washes to dry instantly and blend optically; gesso-primed panels used by tempera and oil require mechanical blending, not optical layering.',
    ],
  ],
  professor: [
    [
      'Critically analyze how the molecular polymerization kinetics of different binding media (walnut vs. linseed vs. safflower oils) determined the feasibility of specific Venetian colorist techniques.',
      [
        'Linseed\'s slow, controllable oxidation enabled wet blending',
        'All oils polymerize identically',
        'Pigment color determines binding medium behavior',
        'Oil choice was purely aesthetic with no technical consequence',
      ],
      0,
      'Walnut oil\'s rapid polymerization and safflower\'s poor drying made them unsuitable for optical wet-blending; linseed\'s controllable kinetics enabled Venetian broken-color effects impossible in other media.',
    ],
    [
      'How did fresco\'s reliance on lime carbonation as its binding mechanism shape the Northern Renaissance abandonment of the technique?',
      [
        'Fresco\'s irreversible set pushed painters toward oil',
        'Fresco is inherently more durable than oil',
        'Northern painters simply preferred oil for aesthetic reasons',
        'Fresco carbonation is instantaneous and can be reversed',
      ],
      0,
      'Once intonaco set, pentimenti were impossible; the shift to oil\'s revisability (drying allows scraping and overpainting) aligned with Renaissance emphasis on intellectual refinement and workshop division.',
    ],
    [
      'In contemporary conservation science, explain the epistemological problem of attributing paintings via pigment analysis when post-production alteration histories are unknown.',
      [
        'Pigment analysis can\'t prove authenticity alone',
        'Pigment analysis definitively proves authorship',
        'Modern pigments are never found in old paintings',
        'Altered paintings always show visible evidence',
      ],
      0,
      'Pigment analysis offers evidence (anachronistic ultramarine suggests 20th-century rework) but cannot prove authenticity alone; fragmentary documentation of restoration histories compounds epistemological uncertainty.',
    ],
    [
      'How did Caravaggio\'s technical choices—high-opacity lead white, rapid-set formulation, single-session execution—shape his approach to modeling form?',
      [
        'High lead-white enabled bold one-sitting modeling',
        'Caravaggio used standard Renaissance techniques',
        'Technical choices had no relationship to his aesthetic',
        'Caravaggio always used slow-drying oils',
      ],
      0,
      'Caravaggio\'s documented fast execution (often one session) required high-opacity, quick-drying paint; this technical limitation enforced direct, emotionally immediate light-shadow modeling without refinement glazes.',
    ],
    [
      'What does recent neutron activation analysis of Rembrandt\'s late works reveal about his sourcing of rare earth element-rich ochres and its relationship to stylistic evolution?',
      [
        'Trace lanthanides suggest finer imported ochres later on',
        'Neutron analysis cannot detect trace elements',
        'Rembrandt\'s pigment sources never changed',
        'Earth element composition is irrelevant to color appearance',
      ],
      0,
      'Neutron activation detected shifted rare-earth profiles in Rembrandt\'s late works; these align with documented increased import of premium ochres to Amsterdam, supporting stylistic dating and sourcing hypotheses.',
    ],
    [
      'Critique the interpretive limits of applying Panofsky\'s three-tier iconographic methodology to 15th-century Netherlandish painting given emerging evidence of workshop-specific symbolic codes.',
      [
        'Guilds had symbolic codes Panofsky missed',
        'Panofsky\'s method is universally applicable',
        'Workshops never developed unique symbolic codes',
        'Iconography is irrelevant to painting analysis',
      ],
      0,
      'Guild-specific heraldry and patron-family symbolic preferences created localized iconographic "dialects"; Panofsky\'s generalized reading misses these intentional, undocumented workshop-level meanings.',
    ],
    [
      'How did late-18th-century landscape painters such as Turner and Friedrich visualize the Kantian concept of the sublime?',
      [
        'Turner and Friedrich visualized Kant\'s sublime',
        'Kant\'s aesthetics have no connection to painting',
        'The sublime in art predates Kant',
        'Landscape painters never engaged with philosophy',
      ],
      0,
      'Turner and Friedrich\'s compositions deliberately embody Kantian sublimity: overwhelming scale, formless vastness, and human insignificance visualize Kant\'s phenomenological description of the mathematically/dynamically sublime.',
    ],
    [
      'In the historiography of art historical methodology, explain how formalist abstraction from iconography (Fry, Greenberg) paradoxically enabled and constrained interpretation of abstract and non-representational works.',
      [
        'Formalism excluded historical context',
        'Formalism and abstraction are unrelated',
        'Formalism enabled all historical interpretations equally',
        'Abstract painting required no critical methodology',
      ],
      0,
      'Formalism freed abstract art from iconographic decoding (necessary) but dogmatically excluded historical, biographical, and material contexts (limiting); this methodological asymmetry still shapes contemporary abstraction criticism.',
    ],
    [
      'What does X-ray diffraction crystallography of medieval gilded surfaces reveal about regional distinctions in gold-beating and leaf thickness that traditional art history cannot access?',
      [
        'Italian gold is coarser than Flemish',
        'All medieval gold leaf is identical',
        'X-ray diffraction cannot measure gold properties',
        'Gold-beating technique was never regionally distinct',
      ],
      0,
      'XRD shows Italian gold exhibits coarser crystallography (thicker beating, less refinement), while Flemish gold is finer (more labor-intensive); these technical signatures reflect workshop resources and trade-network access.',
    ],
    [
      'What combination of factors enabled the scholarly reattribution of works to Artemisia Gentileschi?',
      [
        'Archives erased her; scholarship recovered her work',
        'Gentileschi\'s work was always correctly attributed',
        'Gender never affects art-historical attribution',
        'Archival evidence is irrelevant to attribution',
      ],
      0,
      'Gentileschi\'s recovery depended on integrating archival research (overlooked commission documents), formal analysis (her distinctive chiaroscuro and figural energy), and feminist historiography to dismantle misattribution.',
    ],
    [
      'What was the significance of "tempera-oil emulsion" practice as a transitional technology in 15th-century Flemish painting?',
      [
        'Hybrids mixed each medium\'s advantages',
        'Tempera and oil were never mixed',
        'Emulsion technique was invented in the 19th century',
        'Medium hybridity has no technical significance',
      ],
      0,
      'Netherlandish painters experimentally mixed tempera and oil, exploiting each\'s advantages (fast-set tempera underpainting, oil glaze refinement); technological pragmatism trumped ideological purity.',
    ],
    [
      'In contemporary conservation ethics, why do conservators generally favor reversible in-painting over full aesthetic reconstruction when treating significant paint loss?',
      [
        'Reversibility keeps options open',
        'Reversibility is always ethically superior',
        'Aesthetic completion is always correct',
        'Conservation ethics are irrelevant to painting restoration',
      ],
      0,
      'Reversibility preserves future re-examination but accepts aesthetic incompleteness; integration risks obfuscating evidence and imposing present taste as historical fact. This dilemma has no universal solution.',
    ],
    [
      'How does the optical phenomenon of "halation" (light passing through translucent paint layers and reflecting back) influence the phenomenological experience of old master glazed surfaces in gallery illumination contexts?',
      [
        'Light scattering creates an angle-dependent glow',
        'Halation is a photographic artifact irrelevant to painting',
        'Glazed surfaces are optically static',
        'Gallery lighting has no effect on perception',
      ],
      0,
      'Halation produces the optical "glow" viewers report in Old Masters; light enters transparent glazes, reflects internally, exits at altered angles. This angle-dependent effect explains why reproductions fail to convey experienced luminosity.',
    ],
    [
      'Critically assess how postcolonial art history reframes the "Oriental influences" narrative in Renaissance painting, rejecting diffusionist frameworks that positioned Europe as passive recipient.',
      [
        'Trade exchange was negotiated, not one-way',
        'Renaissance painters copied Oriental art passively',
        'Trade had no influence on European painting technique',
        'Postcolonial criticism is irrelevant to painting history',
      ],
      0,
      'Postcolonial scholarship shows lapis sourcing, Islamic paper use, and glazing techniques were negotiated exchanges, not passive borrowing; Renaissance appropriation involved strategic transformation, not simple imitation.',
    ],
    [
      'In recent scholarship on painter\'s optical tools (camera obscura, prisms, mirrors), explain the epistemological problem: optical verification (mechanical reproduction) versus intentional artistic interpretation.',
      [
        'Optical aids show technical capacity, not creative intent',
        'Optical tools prove paintings are mechanical reproductions',
        'Artists never used optical devices',
        'Optical aids eliminate artistic interpretation entirely',
      ],
      0,
      'Optical device use (verifiable technically) does not entail eliminating artistic agency; painters selected, distorted, and recomposed optically-derived images. Tool presence ≠ authorial passivity.',
    ],
  ],
};
